import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import type { SalesSignalKind } from "@xop/dashboard-shared";

/**
 * Tiny coloured icon + label pair for a single piece of evidence the
 * agent attached to an org lead. Used both inside a card (icons only)
 * and inside the evidence-timeline drawer (icon + label).
 */
// `SalesSignalKind` is a free-form `string` since the v4 senior-manager
// rebuild, so `META` is a string-index map: every lookup (and the
// `other` slot itself) is `T | undefined` under
// `noUncheckedIndexedAccess`. `OTHER` is a standalone definite value
// so the `?? OTHER` fallback resolves to a non-optional type.
const OTHER = { label: "Other", emoji: "•" };

const META: Record<SalesSignalKind, { label: string; emoji: string }> = {
  tender:             { label: "Tender",            emoji: "📄" },
  news:               { label: "News",              emoji: "📰" },
  job_posting:        { label: "Job posting",       emoji: "💼" },
  leadership_change:  { label: "Leadership change", emoji: "🪪" },
  expansion:          { label: "Expansion",         emoji: "🏗" },
  rfi:                { label: "RFI",               emoji: "❓" },
  filing:             { label: "Filing",            emoji: "🏛" },
  other:              OTHER,
};

export function SignalKindIcon({
  kind,
  showLabel = false,
  sx,
}: {
  kind: SalesSignalKind;
  showLabel?: boolean;
  sx?: SxProps<Theme>;
}): JSX.Element {
  const m = META[kind] ?? OTHER;
  return (
    <Box
      component="span"
      title={m.label}
      aria-label={m.label}
      sx={[
        { display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 12 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box component="span" aria-hidden>{m.emoji}</Box>
      {showLabel && <Box component="span">{m.label}</Box>}
    </Box>
  );
}

export function signalKindLabel(k: SalesSignalKind): string {
  return (META[k] ?? OTHER).label;
}
